import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useFormBuilder } from "../hooks/useFormBuilder";
import { getInputStyle, getButtonStyle } from "./formThemeStyles";
import {
    getNextFieldId,
    getFormPath,
    computeCalculatedFields,
    selectThankYouScreen,
} from "../formLogic/evaluateLogic";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const isBooleanField = (field) =>
    field.type === "yes_no" || (field.type === "checkbox" && (!field.options || field.options.length === 0));

const isMultiSelect = (field) =>
    field.type === "multi_choice" || (field.type === "checkbox" && (field.options || []).length > 0);

const isChoiceLike = (field) =>
    isBooleanField(field) || isMultiSelect(field) || ["radio", "dropdown", "select", "rating"].includes(field.type);

// Single-select types where Enter/letter-key/click immediately confirms and advances.
const isSingleSelectAdvance = (field) =>
    isBooleanField(field) || ["radio", "dropdown", "select", "rating"].includes(field.type);

const getOptionsForField = (field, dropdownQuery) => {
    if (isBooleanField(field)) return ["Yes", "No"];
    if (field.type === "rating") {
        if (field.limit === 10) return Array.from({ length: 11 }, (_, i) => String(i)); // NPS 0-10
        const max = field.limit && field.limit > 0 ? field.limit : 5;
        return Array.from({ length: max }, (_, i) => String(i + 1));
    }
    const options = field.options || [];
    if ((field.type === "select" || field.type === "dropdown") && dropdownQuery) {
        return options.filter((o) => o.toLowerCase().includes(dropdownQuery.toLowerCase()));
    }
    return options;
};

const isAnswerEmpty = (value) => {
    if (value === undefined || value === null) return true;
    if (Array.isArray(value)) return value.length === 0;
    return String(value).trim() === "";
};

// FormResponse.responseData is Map<String,String> — multi-select arrays are joined,
// matching the existing classic-renderer convention (handleCheckboxChange).
const serializeAnswers = (answers) => {
    const out = {};
    for (const [key, value] of Object.entries(answers)) {
        out[key] = Array.isArray(value) ? value.join(", ") : value;
    }
    return out;
};

const keyboardHint = (field) => {
    if (isBooleanField(field)) return "Press Y for Yes, N for No, or Enter to confirm";
    if (isMultiSelect(field)) return "Press 1-9 to toggle options, Enter to continue";
    if (["radio", "dropdown", "select", "rating"].includes(field.type)) {
        return "Press a letter to select, ↑↓ to navigate, Enter to confirm";
    }
    if (field.type === "textarea") return "Press Enter to continue, Shift+Enter for a new line";
    if (["statement", "section_break"].includes(field.type)) return "Press Enter ↵ to continue";
    return "Press Esc to clear, Enter ↵ to continue";
};

function ConversationalFormRenderer({ form, formId }) {
    const { submitPublicForm, savePartialProgress, loadPartialProgress } = useFormBuilder();
    const recaptchaRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);

    const fields = useMemo(() => form.fields || [], [form.fields]);
    const theme = form.theme || {};
    const primaryColor = theme.primaryColor || "#6366f1";
    const bgColor = theme.backgroundColor || "#ffffff";
    const fontFamily = theme.fontFamily || "Inter, sans-serif";
    const labelColor = theme.labelColor || "#374151";
    const captchaSiteKey = form.recaptchaSiteKey || process.env.REACT_APP_RECAPTCHA_SITE_KEY;

    const [started, setStarted] = useState(!form.welcomeScreen?.enabled);
    const [currentFieldName, setCurrentFieldName] = useState(() =>
        form.welcomeScreen?.enabled ? null : getNextFieldId(fields, null, {})
    );
    const [answers, setAnswers] = useState({});
    const [history, setHistory] = useState([]);
    const [direction, setDirection] = useState("forward");
    const [animKey, setAnimKey] = useState(0);
    const [highlightIndex, setHighlightIndex] = useState(0);
    const [dropdownQuery, setDropdownQuery] = useState("");
    const [validationError, setValidationError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [thankYou, setThankYou] = useState(null);
    const [resumeData, setResumeData] = useState(null); // { responseData, currentFieldName } from server

    const currentField = fields.find((f) => f.name === currentFieldName) || null;

    const path = useMemo(() => getFormPath(fields, answers), [fields, answers]);
    const currentStepIndex = path.findIndex((f) => f.name === currentFieldName);
    const progress = path.length > 0 && currentStepIndex >= 0
        ? ((currentStepIndex + 1) / path.length) * 100
        : 0;
    const isLastQuestion = currentField
        ? getNextFieldId(fields, currentFieldName, answers) === null
        : false;

    // Reset transient per-question state + autofocus when the question changes
    // (delay matches the 300ms slide animation).
    useEffect(() => {
        setHighlightIndex(0);
        setDropdownQuery("");
        setValidationError("");
        const t = setTimeout(() => inputRef.current?.focus(), 320);
        return () => clearTimeout(t);
    }, [currentFieldName]);

    // On mount: check for a saved partial submission via the session cookie
    useEffect(() => {
        if (form.display_mode !== "conversational") return;
        loadPartialProgress(formId).then(partial => {
            if (partial && partial.currentFieldName) {
                setResumeData(partial);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formId]);

    const handleSubmitForm = useCallback(async (finalAnswers) => {
        setSubmitting(true);
        setSubmitError("");

        let captchaToken = null;
        if (form.captchaEnabled && recaptchaRef.current) {
            captchaToken = recaptchaRef.current.getValue();
            if (!captchaToken) {
                setSubmitError("Please complete the captcha");
                setSubmitting(false);
                return;
            }
        }

        const calc = computeCalculatedFields(fields, finalAnswers);
        const payload = { ...serializeAnswers(finalAnswers), ...calc };
        const result = await submitPublicForm(formId, payload, captchaToken);
        if (result) {
            setThankYou(result.thankYou || selectThankYouScreen(form.thankYouScreens, payload));
            setSubmitted(true);
        } else {
            setSubmitError("Something went wrong. Please try again.");
        }
        setSubmitting(false);
    }, [fields, form, formId, submitPublicForm]);

    const goNext = useCallback((overrideAnswers) => {
        if (!currentField) return;
        const currentAnswers = overrideAnswers || answers;

        if (
            currentField.isRequired &&
            !["statement", "section_break"].includes(currentField.type) &&
            isAnswerEmpty(currentAnswers[currentField.name])
        ) {
            setValidationError("This field is required");
            return;
        }

        const next = getNextFieldId(fields, currentFieldName, currentAnswers);
        setHistory((h) => [...h, currentFieldName]);
        setDirection("forward");
        setAnimKey((k) => k + 1);

        if (next === null) {
            handleSubmitForm(currentAnswers);
        } else {
            setCurrentFieldName(next);
            // Fire-and-forget: save progress so the respondent can resume later
            if (form.display_mode === "conversational") {
                savePartialProgress(formId, {
                    responseData: serializeAnswers(currentAnswers),
                    currentFieldName: next,
                    score: 0,
                });
            }
        }
    }, [currentField, currentFieldName, answers, fields, handleSubmitForm, form.display_mode, formId, savePartialProgress]);

    const goBack = useCallback(() => {
        if (history.length === 0) return;
        const prev = history[history.length - 1];
        setHistory((h) => h.slice(0, -1));
        setDirection("backward");
        setAnimKey((k) => k + 1);
        setCurrentFieldName(prev);
    }, [history]);

    const setAnswer = useCallback((value) => {
        if (!currentField) return;
        setAnswers((prev) => ({ ...prev, [currentField.name]: value }));
    }, [currentField]);

    const advanceWithAnswer = useCallback((value) => {
        if (!currentField) return;
        const newAnswers = { ...answers, [currentField.name]: value };
        setAnswers(newAnswers);
        goNext(newAnswers);
    }, [answers, currentField, goNext]);

    const toggleOption = useCallback((option) => {
        if (!currentField) return;
        const current = Array.isArray(answers[currentField.name]) ? answers[currentField.name] : [];
        const updated = current.includes(option)
            ? current.filter((o) => o !== option)
            : [...current, option];
        setAnswer(updated);
    }, [answers, currentField, setAnswer]);

    const handleStart = useCallback(() => {
        setStarted(true);
        if (currentFieldName === null) {
            setCurrentFieldName(getNextFieldId(fields, null, answers));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentFieldName, fields]);

    const resetAll = useCallback(() => {
        setAnswers({});
        setHistory([]);
        setSubmitted(false);
        setThankYou(null);
        setSubmitError("");
        setDirection("forward");
        setAnimKey((k) => k + 1);
        setCurrentFieldName(getNextFieldId(fields, null, {}));
        setStarted(!form.welcomeScreen?.enabled);
    }, [fields, form.welcomeScreen]);

    // Global keyboard shortcuts (spec A2): Enter to advance, Shift+Enter for newlines,
    // Esc to clear, arrow keys + letter/number keys for choice questions.
    useEffect(() => {
        if (submitted) return;

        const handler = (e) => {
            if (!started) {
                if (e.key === "Enter") {
                    e.preventDefault();
                    handleStart();
                }
                return;
            }
            if (!currentField) return;

            if (e.key === "Enter") {
                if (currentField.type === "textarea" && e.shiftKey) return; // allow newline
                e.preventDefault();
                if (isSingleSelectAdvance(currentField)) {
                    const options = getOptionsForField(currentField, dropdownQuery);
                    if (options.length > 0) {
                        advanceWithAnswer(options[Math.min(highlightIndex, options.length - 1)]);
                        return;
                    }
                }
                goNext();
                return;
            }

            if (e.key === "Escape") {
                e.preventDefault();
                setAnswer(isMultiSelect(currentField) ? [] : "");
                return;
            }

            if (!isChoiceLike(currentField)) return;
            const options = getOptionsForField(currentField, dropdownQuery);

            if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                e.preventDefault();
                setHighlightIndex((i) => Math.min(i + 1, Math.max(options.length - 1, 0)));
                return;
            }
            if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                e.preventDefault();
                setHighlightIndex((i) => Math.max(i - 1, 0));
                return;
            }

            if (isBooleanField(currentField)) {
                if (e.key.toLowerCase() === "y") { e.preventDefault(); advanceWithAnswer("Yes"); return; }
                if (e.key.toLowerCase() === "n") { e.preventDefault(); advanceWithAnswer("No"); return; }
            }

            if (isMultiSelect(currentField)) {
                const num = parseInt(e.key, 10);
                if (num >= 1 && num <= 9 && num <= options.length) {
                    e.preventDefault();
                    toggleOption(options[num - 1]);
                    return;
                }
            }

            if (isSingleSelectAdvance(currentField)) {
                const letterIdx = LETTERS.indexOf(e.key.toUpperCase());
                if (letterIdx >= 0 && letterIdx < options.length) {
                    e.preventDefault();
                    advanceWithAnswer(options[letterIdx]);
                }
            }
        };

        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [started, currentField, dropdownQuery, highlightIndex, submitted, goNext, advanceWithAnswer, toggleOption, setAnswer, handleStart]);

    const renderFileUpload = (field, value, fieldId) => (
        <div
            id={fieldId}
            ref={inputRef}
            role="button"
            tabIndex={0}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
                if (e.key === " ") { e.preventDefault(); fileInputRef.current?.click(); }
            }}
            onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files?.[0];
                if (file) setAnswer(file.name);
            }}
            onDragOver={(e) => e.preventDefault()}
            style={{
                border: `2px dashed ${theme.inputBorderColor || "#d1d5db"}`,
                borderRadius: `${theme.borderRadius || 8}px`,
                padding: "32px",
                textAlign: "center",
                cursor: "pointer",
                minHeight: "120px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: labelColor,
                fontSize: "15px",
            }}
        >
            <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) setAnswer(file.name);
                }}
            />
            {value ? <span>📎 {value}</span> : <span>Drag &amp; drop a file here, or click to browse</span>}
        </div>
    );

    const renderChoiceOptions = (field, value, searchable) => {
        const options = getOptionsForField(field, dropdownQuery);
        const multi = isMultiSelect(field);
        const boolField = isBooleanField(field);
        const selected = multi ? (Array.isArray(value) ? value : []) : value;
        const fieldId = `cf-field-${field.fieldId || field.name}`;

        return (
            <div>
                {searchable && (
                    <input
                        id={fieldId}
                        ref={inputRef}
                        type="text"
                        value={dropdownQuery}
                        onChange={(e) => { setDropdownQuery(e.target.value); setHighlightIndex(0); }}
                        placeholder="Type to search..."
                        style={{ ...getInputStyle(theme), fontSize: "18px", padding: "12px 16px", marginBottom: "12px" }}
                    />
                )}
                <div role={multi ? "group" : "listbox"} aria-label={field.label} style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {options.map((opt, i) => {
                        const isSelected = multi ? selected.includes(opt) : value === opt;
                        const isHighlighted = i === highlightIndex;
                        const badge = field.type === "rating" ? opt : boolField ? opt[0] : multi ? String(i + 1) : LETTERS[i];

                        return (
                            <button
                                key={opt}
                                type="button"
                                ref={i === 0 && !searchable ? inputRef : null}
                                onFocus={() => setHighlightIndex(i)}
                                onClick={() => (multi ? toggleOption(opt) : advanceWithAnswer(opt))}
                                aria-pressed={multi ? isSelected : undefined}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "12px",
                                    minHeight: "48px",
                                    padding: "10px 16px",
                                    borderRadius: `${theme.borderRadius || 8}px`,
                                    border: `2px solid ${isSelected ? primaryColor : (theme.inputBorderColor || "#d1d5db")}`,
                                    backgroundColor: isSelected
                                        ? `${primaryColor}15`
                                        : isHighlighted ? `${primaryColor}08` : (theme.inputBgColor || "#ffffff"),
                                    color: labelColor,
                                    fontSize: "16px",
                                    textAlign: "left",
                                    cursor: "pointer",
                                    width: "100%",
                                }}
                            >
                                <span style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "28px",
                                    height: "28px",
                                    borderRadius: "6px",
                                    border: `1px solid ${theme.inputBorderColor || "#d1d5db"}`,
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    flexShrink: 0,
                                }}>
                                    {badge}
                                </span>
                                <span>{field.type === "rating" ? opt : opt}</span>
                                {isSelected && <span style={{ marginLeft: "auto" }}>✓</span>}
                            </button>
                        );
                    })}
                </div>
                {searchable && options.length === 0 && (
                    <p style={{ color: labelColor, opacity: 0.6, fontSize: "14px" }}>No matching options</p>
                )}
            </div>
        );
    };

    const renderFieldInput = () => {
        const field = currentField;
        const value = answers[field.name];
        const fieldId = `cf-field-${field.fieldId || field.name}`;
        const inputStyle = {
            ...getInputStyle(theme),
            fontSize: "20px",
            padding: "12px 16px",
            minHeight: "48px",
        };

        switch (field.type) {
            case "textarea":
                return (
                    <textarea
                        id={fieldId}
                        ref={inputRef}
                        value={value || ""}
                        onChange={(e) => setAnswer(e.target.value)}
                        rows={4}
                        placeholder={field.placeholder}
                        style={{ ...inputStyle, resize: "vertical" }}
                    />
                );
            case "number":
                return (
                    <input id={fieldId} ref={inputRef} type="number" value={value ?? ""}
                        onChange={(e) => setAnswer(e.target.value)} placeholder={field.placeholder} style={inputStyle} />
                );
            case "email":
                return (
                    <input id={fieldId} ref={inputRef} type="email" value={value || ""}
                        onChange={(e) => setAnswer(e.target.value)} placeholder={field.placeholder} style={inputStyle} />
                );
            case "phone":
                return (
                    <input id={fieldId} ref={inputRef} type="tel" value={value || ""}
                        onChange={(e) => setAnswer(e.target.value)} placeholder={field.placeholder} style={inputStyle} />
                );
            case "url":
                return (
                    <input id={fieldId} ref={inputRef} type="url" value={value || ""}
                        onChange={(e) => setAnswer(e.target.value)} placeholder={field.placeholder} style={inputStyle} />
                );
            case "date":
                return (
                    <input id={fieldId} ref={inputRef} type="date" value={value || ""}
                        onChange={(e) => setAnswer(e.target.value)} style={{ ...inputStyle, maxWidth: "240px" }} />
                );
            case "datetime":
                return (
                    <input id={fieldId} ref={inputRef} type="datetime-local" value={value || ""}
                        onChange={(e) => setAnswer(e.target.value)} style={{ ...inputStyle, maxWidth: "280px" }} />
                );
            case "file_upload":
                return renderFileUpload(field, value, fieldId);
            case "statement":
            case "section_break":
            case "calculated":
                return null;
            case "select":
            case "dropdown":
                return renderChoiceOptions(field, value, true);
            case "radio":
            case "multi_choice":
            case "checkbox":
            case "rating":
            case "yes_no":
                return renderChoiceOptions(field, value, false);
            default:
                return (
                    <input id={fieldId} ref={inputRef} type="text" value={value || ""}
                        onChange={(e) => setAnswer(e.target.value)} placeholder={field.placeholder} style={inputStyle} />
                );
        }
    };

    const outerStyle = {
        minHeight: "100dvh",
        width: "100%",
        backgroundColor: bgColor,
        fontFamily,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
    };

    const screenStyle = {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 16px",
        textAlign: "center",
        boxSizing: "border-box",
    };

    const globalStyles = `
        @keyframes cfSlideInForward { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes cfSlideInBackward { from { opacity: 0; transform: translateY(-24px); } to { opacity: 1; transform: translateY(0); } }
        .cf-slide-forward { animation: cfSlideInForward 300ms ease-out; }
        .cf-slide-backward { animation: cfSlideInBackward 300ms ease-out; }
        button:focus-visible, input:focus-visible, textarea:focus-visible, [tabindex]:focus-visible {
            outline: 2px solid ${primaryColor};
            outline-offset: 2px;
        }
        @media (max-width: 640px) {
            .cf-slide-forward, .cf-slide-backward { animation-duration: 250ms; }
        }
    `;

    const brandingFooter = form.showBranding !== false ? (
        <a
            href="https://contentflashai.com"
            style={{ textAlign: "center", padding: "8px", color: "#9ca3af", fontSize: "12px", textDecoration: "none" }}
        >
            Powered by Content Flash AI
        </a>
    ) : null;

    // Welcome screen
    if (!started) {
        return (
            <div style={outerStyle}>
                <div style={screenStyle}>
                    <div style={{ maxWidth: "640px" }}>
                        <h1 style={{ fontSize: "clamp(28px, 5vw, 48px)", fontWeight: 700, color: labelColor, marginBottom: "16px" }}>
                            {form.welcomeScreen?.title || form.title}
                        </h1>
                        {(form.welcomeScreen?.description || form.description) && (
                            <p style={{ fontSize: "18px", color: labelColor, opacity: 0.75, marginBottom: "32px" }}>
                                {form.welcomeScreen?.description || form.description}
                            </p>
                        )}

                        {resumeData ? (
                            <div>
                                <div style={{
                                    display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px",
                                    padding: "10px 16px", borderRadius: "8px",
                                    backgroundColor: primaryColor + "18", border: `1px solid ${primaryColor}44`,
                                    color: labelColor, fontSize: "14px",
                                }}>
                                    <span>↩</span>
                                    <span>You have a saved response — resume where you left off?</span>
                                </div>
                                <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                                    <button
                                        onClick={() => {
                                            setAnswers(resumeData.responseData || {});
                                            setCurrentFieldName(resumeData.currentFieldName);
                                            setStarted(true);
                                            setResumeData(null);
                                        }}
                                        style={{ ...getButtonStyle(theme, false), fontSize: "16px", padding: "12px 24px", minHeight: "48px" }}
                                    >
                                        Resume →
                                    </button>
                                    <button
                                        onClick={() => { setResumeData(null); handleStart(); }}
                                        style={{
                                            fontSize: "16px", padding: "12px 24px", minHeight: "48px",
                                            background: "transparent", border: `1px solid ${primaryColor}`,
                                            color: primaryColor, borderRadius: "6px", cursor: "pointer",
                                        }}
                                    >
                                        Start fresh
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <button
                                    onClick={handleStart}
                                    style={{ ...getButtonStyle(theme, false), fontSize: "18px", padding: "14px 32px", minHeight: "48px" }}
                                >
                                    {form.welcomeScreen?.buttonText || "Start"} →
                                </button>
                                <p style={{ marginTop: "16px", fontSize: "13px", color: labelColor, opacity: 0.5 }}>Press Enter ↵ to start</p>
                            </>
                        )}
                    </div>
                </div>
                {brandingFooter}
                <style>{globalStyles}</style>
            </div>
        );
    }

    // Thank-you screen
    if (submitted) {
        return (
            <div style={outerStyle}>
                <div style={screenStyle}>
                    <div style={{ maxWidth: "560px" }}>
                        <div style={{
                            width: "72px", height: "72px", borderRadius: "50%", backgroundColor: primaryColor,
                            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px",
                            color: "#fff", fontSize: "32px",
                        }}>✓</div>
                        <h1 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 700, color: labelColor, marginBottom: "12px" }}>
                            {thankYou?.title || "Thank you!"}
                        </h1>
                        <p style={{ fontSize: "16px", color: labelColor, opacity: 0.75, marginBottom: "24px" }}>
                            {thankYou?.description || form.successMessage || "Thank you for your response!"}
                        </p>
                        {thankYou?.buttonUrl ? (
                            <a
                                href={thankYou.buttonUrl}
                                style={{ ...getButtonStyle(theme, false), display: "inline-block", textDecoration: "none" }}
                            >
                                {thankYou.buttonText || "Continue"}
                            </a>
                        ) : thankYou?.buttonText ? (
                            <button onClick={resetAll} style={getButtonStyle(theme, false)}>{thankYou.buttonText}</button>
                        ) : null}
                    </div>
                </div>
                {brandingFooter}
                <style>{globalStyles}</style>
            </div>
        );
    }

    // No questions configured
    if (!currentField) {
        return (
            <div style={outerStyle}>
                <div style={screenStyle}><p style={{ color: labelColor }}>This form has no questions yet.</p></div>
                <style>{globalStyles}</style>
            </div>
        );
    }

    const fieldId = `cf-field-${currentField.fieldId || currentField.name}`;

    return (
        <div style={outerStyle}>
            {/* Resume banner for forms without a welcome screen */}
            {resumeData && (
                <div style={{
                    position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
                    backgroundColor: primaryColor, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: "12px",
                    padding: "10px 20px", fontSize: "14px",
                }}>
                    <span>↩ Resuming where you left off</span>
                    <button
                        onClick={() => {
                            setAnswers(resumeData.responseData || {});
                            setCurrentFieldName(resumeData.currentFieldName);
                            setResumeData(null);
                        }}
                        style={{ background: "rgba(255,255,255,0.25)", border: "1px solid rgba(255,255,255,0.5)", borderRadius: "4px", color: "#fff", padding: "4px 12px", cursor: "pointer", fontSize: "13px" }}
                    >Apply</button>
                    <button
                        onClick={() => setResumeData(null)}
                        style={{ background: "transparent", border: "none", color: "rgba(255,255,255,0.8)", cursor: "pointer", fontSize: "18px", lineHeight: 1 }}
                    >×</button>
                </div>
            )}
            <div style={{ width: "100%", height: "4px", backgroundColor: `${primaryColor}20`, marginTop: resumeData ? "41px" : 0 }}>
                <div style={{ height: "100%", width: `${progress}%`, backgroundColor: primaryColor, transition: "width 0.3s ease" }} />
            </div>
            <div style={screenStyle}>
                <div
                    key={animKey}
                    className={direction === "forward" ? "cf-slide-forward" : "cf-slide-backward"}
                    style={{ maxWidth: "640px", width: "100%", textAlign: "left" }}
                >
                    <div
                        aria-live="polite"
                        style={{ position: "absolute", width: "1px", height: "1px", overflow: "hidden", clip: "rect(0,0,0,0)" }}
                    >
                        {currentField.label}
                    </div>
                    <div style={{ fontSize: "13px", color: labelColor, opacity: 0.6, marginBottom: "8px" }}>
                        Question {currentStepIndex + 1} of {path.length}
                    </div>
                    <label
                        htmlFor={fieldId}
                        style={{ display: "block", fontSize: "clamp(20px, 4vw, 28px)", fontWeight: 700, color: labelColor, marginBottom: "8px" }}
                    >
                        {currentField.label}
                        {currentField.isRequired && <span style={{ color: "#ef4444" }}> *</span>}
                    </label>
                    {currentField.placeholder && !["statement", "section_break"].includes(currentField.type) && (
                        <p style={{ color: labelColor, opacity: 0.6, marginBottom: "16px", fontSize: "15px" }}>{currentField.placeholder}</p>
                    )}
                    {currentField.type === "section_break" && (
                        <div style={{ height: "2px", backgroundColor: `${primaryColor}30`, margin: "16px 0" }} />
                    )}

                    <div style={{ marginTop: "16px" }}>{renderFieldInput()}</div>

                    {validationError && (
                        <p role="alert" style={{ color: "#ef4444", marginTop: "8px", fontSize: "14px" }}>{validationError}</p>
                    )}

                    {form.captchaEnabled && isLastQuestion && captchaSiteKey && (
                        <div style={{ marginTop: "16px" }}>
                            <ReCAPTCHA ref={recaptchaRef} sitekey={captchaSiteKey} />
                        </div>
                    )}

                    {submitError && <p style={{ color: "#ef4444", marginTop: "8px", fontSize: "14px" }}>{submitError}</p>}

                    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "24px" }}>
                        <button
                            type="button"
                            onClick={() => goNext()}
                            disabled={submitting}
                            style={{ ...getButtonStyle(theme, submitting), minHeight: "48px", fontSize: "16px" }}
                        >
                            {submitting ? "Submitting..." : isLastQuestion ? "Submit" : "OK"} <span aria-hidden="true">↵</span>
                        </button>
                        {history.length > 0 && (
                            <button
                                type="button"
                                onClick={goBack}
                                style={{
                                    background: "none", border: "none", color: labelColor, opacity: 0.6,
                                    cursor: "pointer", fontSize: "14px", minHeight: "48px", padding: "0 8px",
                                }}
                            >
                                ← Back
                            </button>
                        )}
                    </div>
                    <p style={{ marginTop: "12px", fontSize: "12px", color: labelColor, opacity: 0.5 }}>
                        {keyboardHint(currentField)}
                    </p>
                </div>
            </div>
            {brandingFooter}
            <style>{globalStyles}</style>
        </div>
    );
}

export default ConversationalFormRenderer;
